/* THE PRICE MOVES IN THE SAME FRAME AS THE CLICK.
 *
 * ── WHAT WAS SLOW, AND WHY IT WAS SLOW ──────────────────────────────────────
 * Nothing here is a performance tweak to a render. The price was not being
 * rendered late; it was being ASKED FOR over the network and then rendered.
 *
 *   `assets/variant-picker.js` → `variantChanged()` → `fetchUpdatedSection()`
 *
 * fires on every `change` from the picker and GETs the whole product page as
 * HTML. `assets/product-price.js` then waits on that request's promise and
 * does `priceContainer.replaceWith(newPrice)`; `assets/sticky-add-to-cart.js`
 * waits on the same promise and morphs the whole bar. So the figure a customer
 * is looking at was gated on a full product-page round trip — a few hundred
 * milliseconds on a good connection and seconds on a bad one, for a number the
 * page could already have stated.
 *
 * ── WHY IT CAN BE INSTANT AT ALL ────────────────────────────────────────────
 * Because the number is already in the document. `blocks/price.liquid` emits
 * `script.rz-variant-prices`: every selectable variant's price, compare-at
 * price, discount percentage, availability and unit price, ALREADY FORMATTED BY
 * LIQUID'S OWN `money` FILTER.
 *
 * FORMATTED BY LIQUID, NOT BY THIS FILE, AND THAT IS THE WHOLE DESIGN. A
 * client-side money formatter would be a SECOND implementation of the shop's
 * money format, and two implementations of one rule is how the figure on the
 * button comes to disagree with the figure on the page. The strings here are
 * byte-identical to what the section render would have produced, which is why
 * the fetch landing behind us changes nothing on screen.
 *
 * ── THE FETCH IS NOT REMOVED, AND MUST NOT BE ───────────────────────────────
 * It still carries the media gallery, the SKU, the cart form's variant id, the
 * delivery block and the instalment widget. It is now a BACKGROUND
 * RECONCILIATION: it arrives, replaces the price container with an identical
 * one, and nothing moves. This file never blanks a price, never writes a
 * placeholder and never removes an element, so there is no state in which the
 * customer sees a gap — requirement 6, and the reason every write below
 * compares before it writes.
 *
 * ── ONE ENTRY POINT, TWO WAYS IN ────────────────────────────────────────────
 *   1. a `change` from any control carrying `data-option-name` whose selected
 *      option carries `data-variant-id` — the size select, the rung cards
 *      (which write that select and dispatch `change` synchronously), the
 *      plain picker on manufacturer and default templates;
 *   2. `window.RugInstantPrice.apply(id)`, called by `rug-size-grid.js` for a
 *      SHAPE press, where the target variant belongs to a shape whose option
 *      values are not in this document at all.
 *
 * THE LISTENER IS ON `window`, NOT ON `document`, AND THAT IS A CONTROL.
 * `rug-size-grid.js` calls `stopImmediatePropagation()` from a document-capture
 * listener when the shape changes, so a second document-capture listener would
 * run or not run depending on which script tag parsed first. Capture reaches
 * `window` before `document`, so this one cannot be ordered out of existence.
 */
(function () {
  'use strict';
  if (window.RugInstantPrice) return;

  var DATA = 'script.rz-variant-prices';
  var CONTAINER = '[ref="priceContainer"]';

  /* SURFACES THAT ARE NOT THIS PRODUCT'S PRICE. `[data-cs-price-mount]` holds
   * `rug-custom-size.js`'s CLONE of the price block, which states the price of
   * the size being typed rather than of the selected variant — writing the
   * variant's price into it would put a figure under the custom form that is
   * not what the customer is about to buy. The rest are other products'. */
  var SKIP = '[data-cs-price-mount], product-card, cart-drawer, .cart-drawer,' +
             ' .menu-drawer, .resource-card, quick-add-dialog';

  var rawText = null, parsed = null;

  /* RE-READ WHEN THE TEXT CHANGES, NOT WHEN THE NODE CHANGES. A shape press
   * morphs `main`, and `morph` updates a script's text IN PLACE — same element,
   * new contents. Caching on identity would serve the old shape's prices for
   * the rest of the page view. */
  function table() {
    var n = document.querySelector(DATA);
    if (!n) return null;
    var raw = n.textContent;
    if (raw === rawText) return parsed;
    try { parsed = JSON.parse(raw); } catch (e) { parsed = null; }
    rawText = raw;
    return parsed;
  }

  function rowFor(id) {
    var t = table();
    return (t && t.v && t.v[String(id)]) || null;
  }

  /* THE FIGURE IS A TEXT NODE AFTER A VISUALLY-HIDDEN LABEL, and the label is
   * the screen-reader announcement ("Sale price", "Regular price"). Rewriting
   * `textContent` would delete it. Same approach `rug-custom-size.js` already
   * uses on its clone, for the same reason. */
  function setNum(el, s) {
    if (!el || s == null) return;
    var n = el.lastChild;
    while (n && n.nodeType !== 3) n = n.previousSibling;
    var want = ' ' + s;
    if (n) { if (n.nodeValue !== want) n.nodeValue = want; }
    else el.appendChild(document.createTextNode(want));
  }

  /* `hidden` ALONE DOES NOT HIDE `.compare-at-price`. It carries
   * `.price-item__group`, and `price-styles.liquid` gives that class
   * `display: inline-block` — a class selector outranks the user-agent's
   * `[hidden] { display: none }`, so the attribute is set and the element stays
   * on screen. Both are written: the attribute for semantics and assistive
   * technology, the property so it is actually gone. */
  function show(el, on) {
    if (!el) return;
    if (el.hidden === !on && el.style.display === (on ? '' : 'none')) return;
    el.hidden = !on;
    el.style.display = on ? '' : 'none';
  }

  /* VOLUME PRICING IS LEFT TO THE FETCH. That branch prints a RANGE
   * ("$60.00 - $70.00") from `quantity_price_breaks`, not the variant's own
   * price, so writing one figure into it would state something false. No rug
   * carries volume pricing; this is a refusal rather than a gap. */
  function isVolume(box) {
    var p = box.parentElement;
    return !!(p && p.querySelector('[ref="volumePricingNote"]'));
  }

  function paintOne(box, row) {
    if (isVolume(box)) return;
    var sale = row[0], cmp = row[1], pct = row[2], unit = row[4];
    var on = !!cmp;

    /* THE BADGE FOLLOWS THE PERCENTAGE AND THE STRUCK PRICE FOLLOWS THE
     * COMPARISON, which is not one gate. `snippets/price.liquid` prints the
     * pair whenever compare-at beats price and the badge only when the
     * truncated percentage is above zero — a $1 saving on a $101 rug is a real
     * struck price and a "0% off" that must not appear. */
    var off = box.querySelector('.rz-off');
    if (off) {
      if (pct > 0) {
        var want = window.rzOffText ? window.rzOffText(pct) : pct + '% off';
        if (off.textContent !== want) off.textContent = want;
      }
      show(off, pct > 0);
    }

    setNum(box.querySelector('.price'), sale);

    var c = box.querySelector('.compare-at-price');
    if (c) { setNum(c, cmp || sale); show(c, on); }

    /* LIMITED TIME OFFER IS AN OFFER AGAINST A STRUCK-THROUGH PRICE. With no
     * compare-at price there is nothing for it to be an offer against, which is
     * exactly the gate `snippets/price.liquid` puts on rendering it at all. */
    show(box.querySelector('.rz-limited'), on);

    var u = box.querySelector('.unit-price');
    if (u) { if (unit) { setNum(u, unit); show(u, true); } else show(u, false); }
  }

  /* THE BUTTON IS DISABLED ON AN UNAVAILABLE VARIANT AND RE-ENABLED ONLY IF WE
   * WERE THE ONES WHO DISABLED IT. `add-to-cart-component` and `product-form.js`
   * disable the button for reasons of their own — mid-add, a maxed line — and
   * re-enabling one of those would hand the customer a button that is not ready.
   * The mark says which disabling is ours to undo.
   *
   * THE LABEL IS NOT REWRITTEN. It comes from a block setting and a translation
   * and the fetch brings the right one within the second; a disabled button
   * still reading "Add to cart" for that second is late, not wrong, and this
   * file has no business inventing copy. */
  function paintAvailability(scope, avail) {
    if (avail !== 0 && avail !== 1) return;
    var btns = scope.querySelectorAll('button[ref="addToCartButton"]');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (b.closest(SKIP)) continue;
      if (!avail) {
        if (!b.disabled) { b.disabled = true; b.setAttribute('data-rz-ip-off', ''); }
      } else if (b.hasAttribute('data-rz-ip-off')) {
        b.disabled = false;
        b.removeAttribute('data-rz-ip-off');
      }
    }
  }

  /* SHOPIFY PRICES THE INSTALMENT WIDGET ITSELF, so the only thing that can be
   * done early is tell it which variant to price. `<shopify-payment-terms>`
   * re-reads the hidden input on `change`; that is Shopify's own refresh and it
   * makes its own request, which is why the instalment line is the one part of
   * this that cannot be synchronous. Starting it now rather than after our
   * fetch is the whole of the gain. */
  function paintTerms(scope, id) {
    var inputs = scope.querySelectorAll('.payment-terms input[name="id"]');
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      if (el.closest(SKIP)) continue;
      if (el.value === String(id)) continue;
      el.value = String(id);
      el.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* THE SCOPE IS THE SECTION THE CONTROL LIVES IN, which is how
   * `product-price.js` scopes its own update. A collection page carries a price
   * per card and a quick-add dialog of its own; a product page carries the buy
   * column and the sticky bar, and both are in one section. */
  function scopeOf(from) {
    if (from && from.closest) {
      var s = from.closest('dialog') || from.closest('.shopify-section');
      if (s) return s;
    }
    return document;
  }

  /**
   * Write a variant's price everywhere this product states one. Returns whether
   * the variant was known — a caller may use that to decide nothing more.
   *
   * @param {string|number} variantId
   * @param {Element} [from] the control that changed, for scoping
   * @returns {boolean}
   */
  function apply(variantId, from) {
    if (!variantId) return false;
    var row = rowFor(variantId);
    if (!row) return false;

    var scope = scopeOf(from);
    var boxes = scope.querySelectorAll(CONTAINER);
    var painted = 0;
    for (var i = 0; i < boxes.length; i++) {
      if (boxes[i].closest(SKIP)) continue;
      paintOne(boxes[i], row);
      painted++;
    }
    paintAvailability(scope, row[3]);
    paintTerms(scope, variantId);
    return painted > 0;
  }

  /* WHICH VARIANT A CONTROL NOW NAMES. `data-variant-id` is populated by Liquid
   * for the LAST option only — the size — because that is the only option whose
   * values resolve to a single variant. A shape radio carries none, which is
   * why `rug-size-grid.js` calls `apply()` directly for a shape press. */
  function variantOf(el) {
    if (!el || !el.dataset) return null;
    if (el.tagName === 'SELECT') {
      var o = el.options[el.selectedIndex];
      if (!o || !o.dataset || !o.dataset.optionName) return null;
      return o.dataset.variantId || null;
    }
    if (!el.dataset.optionName) return null;
    return el.dataset.variantId || null;
  }

  window.addEventListener('change', function (e) {
    var id = variantOf(e.target);
    if (id) apply(id, e.target);
  }, true);

  window.RugInstantPrice = { apply: apply, table: table };
})();
